import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import AdminLayout from '../AdminLayout';
import { getUsers } from '../../api/auth';
import { transfer } from '../../api/transfer';
import { useAuthContext } from '../../contexts/AuthContext';

const SendMoney = () => {
  const { user } = useAuthContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await getUsers();
        if (response.users) {
          setUsers(response.users);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setSendError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSendMoney = async () => {
    if (!selectedUserId || !amount) {
      setSendError('Please select a user and enter an amount');
      return;
    }

    const selectedUser = users.find(u => u.id === selectedUserId);
    if (!selectedUser) {
      setSendError('Selected user not found');
      return;
    }

    if (parseFloat(amount) <= 0) {
      setSendError('Amount must be greater than 0');
      return;
    }

    try {
      setSending(true);
      setSendError(null);
      setSendSuccess(false);

      const transferData = {
        userId: user?.id,
        amount: parseFloat(amount),
        receiverEmail: selectedUser.email || undefined,
        receiverPhone: selectedUser.phone || undefined,
      };

      const response = await transfer(transferData);
      
      if (response.success || response.message) {
        setSendSuccess(true);
        setAmount('');
        setSelectedUserId('');
        // Refresh users list to show updated balances
        const updatedResponse = await getUsers();
        if (updatedResponse.users) {
          setUsers(updatedResponse.users);
        }
        // Hide success message after 3 seconds
        setTimeout(() => {
          setSendSuccess(false);
        }, 3000);
      } else {
        setSendError(response.error || 'Failed to send money');
      }
    } catch (err) {
      console.error('Error sending money:', err);
      setSendError(err.response?.data?.error || err.message || 'Failed to send money. Please try again.');
      setSendSuccess(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4 lg:space-y-6 min-w-[600px]">
        {/* Header */}
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800">Send Money</h2>
          <p className="text-xs lg:text-sm text-gray-600 mt-1">Send value to users</p>
        </div>

        {/* Send Money Section */}
        <Card className="p-6 bg-white border border-gray-200 shadow-lg">
          <h3 className="text-xl font-bold text-gray-800 mb-6">Send Value</h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#586BFF]" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select User */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select User
                </label>
                <Select 
                  value={selectedUserId} 
                  onValueChange={(value) => {
                    setSelectedUserId(value);
                    setSendError(null);
                    setSendSuccess(false);
                  }}
                >
                  <SelectTrigger className="w-full h-12 bg-white border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-[#586BFF]">
                    <SelectValue placeholder="-- Select a user --" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-[300px]">
                    {users.map((userItem) => (
                      <SelectItem
                        key={userItem.id}
                        value={userItem.id}
                        className="hover:bg-gray-100 focus:bg-gray-100"
                      >
                        {userItem.username || userItem.email || userItem.phone || 'Unknown User'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount
                </label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setSendError(null);
                    setSendSuccess(false);
                  }}
                  className="w-full h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#586BFF]"
                />
              </div>

              {/* Send Value Button */}
              <Button
                onClick={handleSendMoney}
                disabled={sending || !selectedUserId || !amount}
                className="w-full h-12 bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white font-semibold text-base shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send Value'
                )}
              </Button>

              {/* Success Message */}
              {sendSuccess && (
                <p className="text-green-600 text-sm font-medium text-center">
                  Value sent successfully!
                </p>
              )}

              {/* Error Message */}
              {sendError && !sendSuccess && (
                <p className="text-red-600 text-sm font-medium text-center">
                  {sendError}
                </p>
              )}
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default SendMoney;

